import type {
  AbilityCategory,
  ModuleDefinition,
  ModuleTier,
  SynergyAbilityModifier,
  SynergyDefinition,
} from "./phase-three-types";
import {
  formatAttackIntervalSeconds,
  normalizeModuleAttackCadence,
} from "./attack-interval";
import { normalizeModuleSkillRanges } from "./skill-range";

interface Theme {
  id: string;
  name: string;
  tag: string;
}

interface AbilityPath {
  id: string;
  theme: Theme;
  track: "strike" | "domain" | "legacy";
  category: AbilityCategory;
  index: number;
}

const THEMES: Theme[] = [
  { id: "flame", name: "炎阳", tag: "fire" },
  { id: "frost", name: "霜月", tag: "frost" },
  { id: "storm", name: "雷云", tag: "lightning" },
  { id: "stone", name: "山岳", tag: "earth" },
  { id: "shadow", name: "幽影", tag: "shadow" },
  { id: "holy", name: "圣辉", tag: "holy" },
  { id: "nature", name: "森灵", tag: "nature" },
  { id: "poison", name: "翠毒", tag: "poison" },
  { id: "blood", name: "血月", tag: "blood" },
  { id: "arcane", name: "奥术", tag: "arcane" },
  { id: "celestial", name: "群星", tag: "celestial" },
  { id: "abyss", name: "深渊", tag: "abyss" },
  { id: "beast", name: "荒兽", tag: "beast" },
  { id: "construct", name: "机巧", tag: "construct" },
  { id: "ocean", name: "潮汐", tag: "ocean" },
  { id: "sand", name: "流沙", tag: "sand" },
  { id: "plague", name: "疫雾", tag: "plague" },
  { id: "spirit", name: "先灵", tag: "spirit" },
  { id: "dragon", name: "古龙", tag: "dragon" },
];

const LEGACY_CATEGORIES: AbilityCategory[] = [
  "labor",
  "finance",
  "pasture",
  "archaeology",
  "enhancement",
  "follower",
  "summon",
  "healing",
  "growth",
  "one_shot",
  "guard",
  "special",
  "elite_attack",
  "finance",
  "pasture",
  "archaeology",
  "enhancement",
  "follower",
  "summon",
];

const PATHS: AbilityPath[] = THEMES.flatMap((theme, themeIndex) => [
  {
    id: `${theme.id}_strike`,
    theme,
    track: "strike" as const,
    category: themeIndex % 5 === 4 ? "elite_attack" as const : "attack" as const,
    index: themeIndex * 3,
  },
  {
    id: `${theme.id}_domain`,
    theme,
    track: "domain" as const,
    category: "area" as const,
    index: themeIndex * 3 + 1,
  },
  {
    id: `${theme.id}_legacy`,
    theme,
    track: "legacy" as const,
    category: LEGACY_CATEGORIES[themeIndex],
    index: themeIndex * 3 + 2,
  },
]);

const LOWER_ADDITIONS: Record<1 | 2 | 3 | 4, number> = {
  1: 57,
  2: 53,
  3: 49,
  4: 39,
};

const STRIKE_NAMES = ["飞针", "符矢", "秘枪", "圣弩", "王权弓", "神罚星"];
const DOMAIN_NAMES = ["微光", "法阵", "领域", "天象", "圣域", "创世界"];
const LEGACY_NAMES = ["碎片", "遗物", "秘宝", "圣器", "王冠", "神权"];
const TIER_SCALE = [0, 1, 1.9, 3.4, 5.7, 9, 14];

function moduleId(path: AbilityPath, tier: ModuleTier): string {
  return `exp_${path.id}_t${tier}`;
}

function moduleName(path: AbilityPath, tier: ModuleTier): string {
  const suffix =
    path.track === "strike"
      ? STRIKE_NAMES[tier - 1]
      : path.track === "domain"
        ? DOMAIN_NAMES[tier - 1]
        : LEGACY_NAMES[tier - 1];
  return `${path.theme.name}${suffix}`;
}

function takeDistributed<T>(values: T[], count: number): T[] {
  if (count >= values.length) {
    return [...values];
  }
  return Array.from({ length: count }, (_, index) => {
    const selected = Math.floor((index * values.length) / count);
    return values[selected];
  });
}

function createModule(path: AbilityPath, tier: ModuleTier): ModuleDefinition {
  const scale = TIER_SCALE[tier];
  const variation = path.index % 5;
  const definition: ModuleDefinition = {
    id: moduleId(path, tier),
    name: moduleName(path, tier),
    description: "",
    tier,
    category: path.category,
    tags: [
      path.theme.tag,
      path.track,
      path.category,
      tier >= 5 ? "mythic" : "expanded",
    ],
    weight: tier === 6 ? 0 : Math.max(22, 105 - tier * 9),
    hidden: tier < 6 && path.index % 23 === 0,
    bonuses: {},
  };

  switch (path.category) {
    case "attack":
      definition.combat = {
        damage: Math.round((9 + variation * 1.4) * scale),
        intervalMs: Math.max(260, 860 - tier * 72 + variation * 24),
        range: 340 + tier * 58,
        target: "random",
        projectiles: 1 + (tier >= 3 && variation % 2 === 0 ? 1 : 0),
        chainTargets: tier >= 5 ? 2 : undefined,
      };
      break;
    case "elite_attack":
      definition.combat = {
        damage: Math.round((17 + variation * 2.2) * scale),
        intervalMs: Math.max(360, 1_120 - tier * 70),
        range: 390 + tier * 65,
        target: "elite_only",
        pierceTargets: tier >= 4 ? 1 : undefined,
      };
      break;
    case "area":
      definition.combat = {
        damage: Math.round((6.5 + variation) * scale),
        intervalMs: Math.max(340, 1_060 - tier * 76 + variation * 30),
        range: 320 + tier * 52,
        target: "random_area",
        radius: 70 + tier * 26 + variation * 8,
        slowRatio: path.theme.tag === "frost" ? 0.18 + tier * 0.015 : undefined,
        slowDurationMs: path.theme.tag === "frost" ? 1_500 : undefined,
      };
      break;
    case "labor": {
      const triggers = ["move_distance", "attack", "repair", "excavate"] as const;
      const trigger = triggers[variation % triggers.length];
      definition.economy = {
        trigger,
        distance: trigger === "move_distance" ? Math.max(90, 220 - tier * 18) : undefined,
        gold: 1 + tier,
        experience: tier,
        crystalChance: tier >= 4 ? 0.01 * (tier - 3) : undefined,
      };
      break;
    }
    case "finance":
      definition.economy = {
        trigger: "periodic",
        intervalMs: Math.max(5_000, 14_000 - tier * 1_200),
        gold: Math.round((3 + variation) * scale),
        experience: Math.round(tier * 1.5),
        condition: variation % 2 === 0 ? "mana_above_half" : undefined,
      };
      break;
    case "pasture":
      definition.companion = {
        kind: "pasture",
        presentationId: `pasture.${path.theme.id}`,
        count: 1 + (tier >= 4 ? 1 : 0),
        orbitRadius: 120 + variation * 15,
        moveSpeed: 115 + tier * 9,
        incomeIntervalMs: Math.max(4_800, 11_000 - tier * 800),
        incomeGold: Math.round(2.4 * scale),
      };
      break;
    case "archaeology":
      definition.archaeology = {
        revealRadius: 80 + tier * 85,
        yieldMultiplier: 0.08 * tier + variation * 0.015,
        wageShare: 0.006 * tier,
      };
      definition.bonuses = {
        revealRadius: 80 + tier * 85,
        archaeologyMultiplier: 0.08 * tier,
        wageShareAdd: 0.006 * tier,
      };
      break;
    case "enhancement":
      definition.bonuses = {
        damageAdd: Math.round(5.5 * scale),
        maxHealthAdd: Math.round(18 * scale),
        armorAdd: Math.round(tier * 1.8),
        fireIntervalMultiplier: tier >= 4 ? 0.94 : undefined,
      };
      break;
    case "follower":
      definition.companion = {
        kind: "follower",
        presentationId: `follower.${path.theme.id}`,
        count: 1 + (tier >= 5 ? 1 : 0),
        orbitRadius: 105 + variation * 12,
        moveSpeed: 150 + tier * 9,
        damage: Math.round((5 + variation) * scale),
        intervalMs: Math.max(330, 960 - tier * 70),
        range: 300 + tier * 48,
      };
      break;
    case "summon":
      definition.bonuses = {
        manaMaxAdd: tier * 14,
        manaRegenAdd: tier >= 4 ? 0.45 * (tier - 3) : undefined,
        summonCapAdd: tier >= 3 ? tier - 2 : undefined,
      };
      definition.companion = {
        kind: "summon",
        presentationId: `summon.${path.theme.id}`,
        count: 1 + Math.floor(tier / 2),
        orbitRadius: 240,
        moveSpeed: 155 + tier * 12,
        damage: Math.round((7 + variation) * scale),
        intervalMs: Math.max(380, 1_020 - tier * 70),
        range: 280 + tier * 45,
        health: Math.round(55 * scale),
        armor: tier * 3,
        manaCost: 8 + tier * 5,
        spawnIntervalMs: Math.max(6_000, 13_000 - tier * 800),
      };
      break;
    case "healing":
      definition.healing = {
        target: tier >= 3 ? "team" : "self",
        amount: Math.round((10 + variation * 2) * scale),
        intervalMs: Math.max(2_500, 7_500 - tier * 600),
        range: 360 + tier * 55,
      };
      break;
    case "growth":
      definition.growth = {
        event: variation % 2 === 0 ? "kill" : "attack",
        every: Math.max(12, 42 - tier * 5),
        stat: variation % 2 === 0 ? "damage" : "maxHealth",
        amount: tier >= 4 ? tier : Math.max(1, tier - 1),
        cap: Math.round(30 * scale),
      };
      break;
    case "one_shot":
      definition.onAcquire = {
        gold: Math.round(22 * scale),
        experience: Math.round(34 * scale),
        damage: Math.round(2.2 * scale),
        maxHealth: Math.round(8 * scale),
      };
      break;
    case "guard":
      definition.companion = {
        kind: "guard",
        presentationId: `guard.${path.theme.id}`,
        count: 1 + (tier >= 5 ? 1 : 0),
        orbitRadius: 150,
        moveSpeed: 135 + tier * 8,
        damage: Math.round((6 + variation) * scale),
        intervalMs: Math.max(420, 1_040 - tier * 65),
        range: 250 + tier * 42,
        health: Math.round(95 * scale),
        armor: 5 + tier * 5,
        respawnMs: Math.max(6_000, 17_000 - tier * 1_500),
      };
      break;
    case "special":
      definition.bonuses = {
        moveSpeedAdd: 8 * tier,
        dodgeAdd: 0.012 * tier,
        scoutingRadiusAdd: 55 * tier,
        crystalDropChance: 0.002 * tier,
      };
      break;
  }

  const normalized = normalizeModuleSkillRanges(
    normalizeModuleAttackCadence(definition),
  );
  normalized.description = describeExpandedModule(normalized);
  return normalized;
}

export function describeExpandedModule(module: ModuleDefinition): string {
  if (module.combat) {
    const combat = module.combat;
    return `每${formatAttackIntervalSeconds(combat.intervalMs)}秒造成${combat.damage}点伤害，射程${combat.range}` +
      `${combat.projectiles && combat.projectiles > 1 ? `，发射${combat.projectiles}枚` : ""}` +
      `${combat.radius ? `，覆盖半径${combat.radius}` : ""}。`;
  }
  if (module.companion) {
    const companion = module.companion;
    if (companion.kind === "pasture") {
      return `召来${companion.count}只牧灵，每${(
        (companion.incomeIntervalMs ?? 10_000) / 1_000
      ).toFixed(1)}秒获得${companion.incomeGold}金币。`;
    }
    return `召来${companion.count}名${categoryName(module.category)}单位，` +
      `造成${companion.damage ?? 0}点伤害，攻击间隔${formatAttackIntervalSeconds(
        companion.intervalMs ?? 1_000,
      )}秒。`;
  }
  if (module.economy) {
    const economy = module.economy;
    return economy.trigger === "periodic"
      ? `每${((economy.intervalMs ?? 10_000) / 1_000).toFixed(1)}秒获得` +
          `${economy.gold ?? 0}金币与${economy.experience ?? 0}灵息。`
      : `完成${laborName(economy.trigger)}时获得${economy.gold ?? 0}金币与` +
          `${economy.experience ?? 0}灵息。`;
  }
  if (module.healing) {
    return `每${(module.healing.intervalMs / 1_000).toFixed(1)}秒恢复` +
      `${module.healing.amount}点生命。`;
  }
  if (module.growth) {
    return `每达成${module.growth.every}次条件，永久增加${module.growth.amount}点` +
      `${module.growth.stat === "damage" ? "攻击" : "生命"}。`;
  }
  if (module.onAcquire) {
    return `获得时立即取得${module.onAcquire.gold ?? 0}金币、` +
      `${module.onAcquire.experience ?? 0}灵息并永久强化本体。`;
  }
  if (module.archaeology) {
    return `扩大反隐范围并使考古收益提高${Math.round(
      (module.archaeology.yieldMultiplier ?? 0) * 100,
    )}%。`;
  }
  return `将${module.name}融入本体，强化攻击、生存或探索能力。`;
}

function categoryName(category: AbilityCategory): string {
  if (category === "mining") return "采矿";
  return {
    attack: "攻击",
    area: "群攻",
    elite_attack: "特攻",
    labor: "打工",
    finance: "理财",
    pasture: "放牧",
    archaeology: "考古",
    enhancement: "强化",
    follower: "随从",
    summon: "召唤",
    healing: "治疗",
    growth: "成长",
    one_shot: "单次",
    guard: "护卫",
    special: "特殊",
  }[category];
}

function laborName(
  trigger:
    | "move_distance"
    | "attack"
    | "repair"
    | "excavate",
): string {
  return {
    move_distance: "移动里程",
    attack: "攻击",
    repair: "修理",
    excavate: "考古",
  }[trigger];
}

const lowerModules = ([1, 2, 3, 4] as const).flatMap((tier) =>
  takeDistributed(PATHS, LOWER_ADDITIONS[tier]).map((path) =>
    createModule(path, tier),
  ),
);

const ASCENSION_PATHS = takeDistributed(PATHS, 24);
const ascensionModules = ASCENSION_PATHS.flatMap((path) => {
  const fifth = createModule(path, 5);
  const sixth = createModule(path, 6);
  fifth.evolutionTo = sixth.id;
  sixth.evolutionFrom = fifth.id;
  fifth.description += ` 可固定升格为「${sixth.name}」。`;
  sixth.description = `由「${fifth.name}」唯一升格。${sixth.description}`;
  return [fifth, sixth];
});

export const EXPANDED_MODULES: ModuleDefinition[] = [
  ...lowerModules,
  ...ascensionModules,
];

function modifiersForPair(
  firstId: string,
  secondId: string,
  pattern: number,
): SynergyAbilityModifier[] {
  const patterns: SynergyAbilityModifier[][] = [
    [
      { moduleIds: [firstId], damageMultiplier: 1.52, extraTargetsAdd: 1 },
      { moduleIds: [secondId], damageMultiplier: 0.78, radiusAdd: 90 },
    ],
    [
      { moduleIds: [firstId], intervalMultiplier: 0.64, manaCostMultiplier: 1.35 },
      { moduleIds: [secondId], rangeMultiplier: 1.48 },
    ],
    [
      { moduleIds: [firstId], projectilesAdd: 1, damageMultiplier: 0.82 },
      { moduleIds: [secondId], intervalMultiplier: 0.74 },
    ],
    [
      { moduleIds: [firstId], radiusAdd: 135, rangeMultiplier: 0.86 },
      { moduleIds: [secondId], damageMultiplier: 1.42 },
    ],
    [
      {
        moduleIds: [firstId],
        companionCountAdd: 1,
        companionHealthMultiplier: 1.5,
      },
      { moduleIds: [secondId], damageMultiplier: 1.36, intervalMultiplier: 1.12 },
    ],
    [
      { moduleIds: [firstId], extraTargetsAdd: 2, intervalMultiplier: 1.18 },
      { moduleIds: [secondId], intervalMultiplier: 0.7, damageMultiplier: 0.86 },
    ],
  ];
  return patterns[pattern % patterns.length];
}

const synergyCandidates = EXPANDED_MODULES.filter(
  (module) =>
    module.tier <= 5 &&
    (module.combat !== undefined || module.companion !== undefined),
);

const duetSynergies: SynergyDefinition[] = Array.from(
  { length: 54 },
  (_, index) => {
    const first =
      synergyCandidates[(index * 5 + 7) % synergyCandidates.length];
    let second =
      synergyCandidates[(index * 7 + 19) % synergyCandidates.length];
    if (second.id === first.id) {
      second =
        synergyCandidates[(synergyCandidates.indexOf(second) + 1) %
          synergyCandidates.length];
    }
    const pattern = index % 6;
    const descriptions = [
      `${first.name}获得弹射与爆发；${second.name}牺牲单体伤害换取更大覆盖。`,
      `${first.name}以更高魔耗极速施放；${second.name}转为超远程支援。`,
      `${first.name}分裂为双发但单发减弱；${second.name}显著加快攻击。`,
      `${first.name}缩短射程换取大范围；${second.name}专注高额单体伤害。`,
      `${first.name}增添一名高生命伙伴；${second.name}以较慢频次打出重击。`,
      `${first.name}攻击变慢但额外追踪两个目标；${second.name}高速连击但单击减弱。`,
    ];
    return {
      id: `exp_duet_${index + 1}`,
      name: `双印·${first.name.slice(0, 2)}${second.name.slice(0, 2)}`,
      description: descriptions[pattern],
      requirements: [
        { kind: "module", moduleId: first.id },
        { kind: "module", moduleId: second.id },
      ],
      bonuses: {},
      abilityModifiers: modifiersForPair(first.id, second.id, pattern),
    };
  },
);

const triadSynergies: SynergyDefinition[] = Array.from(
  { length: 12 },
  (_, index) => {
    const first = synergyCandidates[(index * 5 + 3) % synergyCandidates.length];
    const second =
      synergyCandidates[(index * 7 + 31) % synergyCandidates.length];
    const third =
      synergyCandidates[(index * 11 + 67) % synergyCandidates.length];
    const modules = [...new Map(
      [first, second, third].map((module) => [module.id, module]),
    ).values()];
    for (const candidate of synergyCandidates) {
      if (modules.length >= 3) {
        break;
      }
      if (!modules.some((module) => module.id === candidate.id)) {
        modules.push(candidate);
      }
    }
    return {
      id: `exp_triad_${index + 1}`,
      name: `三相·${modules.map((module) => module.name.slice(0, 1)).join("")}`,
      description:
        `${modules[0].name}负责扩散，${modules[1].name}负责连击，` +
        `${modules[2].name}牺牲射程换取终结伤害。`,
      requirements: modules.map((module) => ({
        kind: "module" as const,
        moduleId: module.id,
      })),
      bonuses: {},
      abilityModifiers: [
        { moduleIds: [modules[0].id], radiusAdd: 170, damageMultiplier: 0.8 },
        {
          moduleIds: [modules[1].id],
          projectilesAdd: 1,
          intervalMultiplier: 0.78,
        },
        {
          moduleIds: [modules[2].id],
          damageMultiplier: 1.75,
          rangeMultiplier: 0.72,
        },
      ],
    };
  },
);

const fifthTier = EXPANDED_MODULES.filter((module) => module.tier === 5);
const apexSynergies: SynergyDefinition[] = Array.from(
  { length: 6 },
  (_, index) => {
    const modules = [
      fifthTier[index * 4],
      fifthTier[index * 4 + 1],
      fifthTier[index * 4 + 2],
    ];
    return {
      id: `exp_apex_${index + 1}`,
      name: `神话·${modules.map((module) => module.name.slice(0, 2)).join("")}`,
      description:
        `${modules.map((module) => module.name).join("、")}组成终局权能：` +
        `首印多重施法，次印扩大领域，末印以更高魔耗发动毁灭重击。`,
      requirements: modules.map((module) => ({
        kind: "one_of" as const,
        moduleIds: [module.id, module.evolutionTo!],
      })),
      bonuses: { damageMultiplier: 0.18, companionDamageMultiplier: 0.2 },
      abilityModifiers: [
        {
          moduleIds: [modules[0].id, modules[0].evolutionTo!],
          projectilesAdd: 2,
          intervalMultiplier: 0.78,
        },
        {
          moduleIds: [modules[1].id, modules[1].evolutionTo!],
          radiusAdd: 240,
          extraTargetsAdd: 2,
        },
        {
          moduleIds: [modules[2].id, modules[2].evolutionTo!],
          damageMultiplier: 2.1,
          manaCostMultiplier: 1.65,
          intervalMultiplier: 1.2,
        },
      ],
      apex: true,
    };
  },
);

export const EXPANDED_SYNERGIES: SynergyDefinition[] = [
  ...duetSynergies,
  ...triadSynergies,
  ...apexSynergies,
];
